import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Brain, CheckCircle, XCircle, Clock, Zap, Users, FileText, TrendingUp, Shield } from 'lucide-react';

const API = `${""}/api`;

const KnowledgeAdmin = ({ onBack }) => {
  const [tab, setTab] = useState('suggestions');
  const [suggestions, setSuggestions] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({});
  const [suggestionStats, setSuggestionStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const editRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sugRes, knowRes, fbRes, statsRes] = await Promise.all([
        axios.get(`${API}/dr-peptide/knowledge-suggestions`),
        axios.get(`${API}/dr-peptide/knowledge`),
        axios.get(`${API}/dr-peptide/feedback/review?status=all&limit=50`),
        axios.get(`${API}/dr-peptide/intelligence-stats`),
      ]);
      setSuggestions(sugRes.data.suggestions || []);
      setSuggestionStats(sugRes.data.stats || {});
      setKnowledge(knowRes.data.knowledge_entries || []);
      setFeedback(fbRes.data.feedback_entries || []);
      setStats(statsRes.data.stats || {});
    } catch (e) { console.error('Load error:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approveSuggestion = async (id, customRule) => {
    try {
      await axios.post(`${API}/dr-peptide/knowledge-suggestions/${id}/approve`, customRule ? { rule: customRule } : {});
      load();
    } catch (e) { toast.error('Error approving'); }
  };

  const rejectSuggestion = async (id) => {
    try {
      await axios.post(`${API}/dr-peptide/knowledge-suggestions/${id}/reject`);
      load();
    } catch (e) { toast.error('Error rejecting'); }
  };

  const toggleKnowledge = async (id) => {
    try {
      await axios.post(`${API}/dr-peptide/knowledge/${id}/toggle`);
      load();
    } catch (e) { toast.error('Error toggling'); }
  };

  const deleteKnowledge = async (id) => {
    if (!window.confirm('Remove this rule from Dr. Peptide\'s intelligence?')) return;
    try {
      await axios.delete(`${API}/dr-peptide/knowledge/${id}`);
      load();
    } catch (e) { toast.error('Error deleting'); }
  };

  const TABS = [
    { id: 'suggestions', label: 'Review Queue', icon: Clock, count: suggestionStats.pending || 0 },
    { id: 'knowledge', label: 'Active Rules', icon: Brain, count: knowledge.filter(k => k.enabled).length },
    { id: 'feedback', label: 'Raw Feedback', icon: FileText, count: feedback.length },
    { id: 'stats', label: 'Intelligence', icon: TrendingUp },
  ];

  const CATEGORY_COLORS = {
    dosing: 'bg-teal-100 text-blue-800', safety: 'bg-red-100 text-red-800', preference: 'bg-purple-100 text-purple-800',
    interaction: 'bg-yellow-100 text-yellow-800', outcome: 'bg-green-100 text-green-800', protocol: 'bg-indigo-100 text-indigo-800',
    general: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="knowledge-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2"><Brain className="h-7 w-7 text-teal-600" /> Clinical Intelligence Manager</h1>
          <p className="text-sm text-gray-500 mt-1">Review practitioner feedback, approve rules, and track how Dr. Peptide evolves</p>
        </div>
        {onBack && <Button variant="outline" size="sm" onClick={onBack}>Back</Button>}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b pb-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${tab === t.id ? 'bg-white border border-b-white -mb-px text-teal-700' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon className="h-4 w-4" /> {t.label}
            {t.count > 0 && <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-100'}`}>{t.count}</span>}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-8 text-gray-500">Loading...</div>}

      {/* ── SUGGESTIONS TAB ── */}
      {!loading && tab === 'suggestions' && (
        <div className="space-y-3">
          {suggestions.filter(s => s.status === 'pending_review').length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-300" />
              <p className="font-medium">All caught up</p>
              <p className="text-sm">No pending suggestions to review. Feedback from practitioners will appear here.</p>
            </div>
          )}
          {suggestions.map(s => (
            <Card key={s.id} className={`${s.status === 'approved' ? 'opacity-60' : s.status === 'rejected' ? 'opacity-40' : ''}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={CATEGORY_COLORS[s.category] || CATEGORY_COLORS.general}>{s.category}</Badge>
                      <Badge variant="outline" className="text-xs">{s.confidence} confidence</Badge>
                      {s.status !== 'pending_review' && <Badge variant={s.status === 'approved' ? 'default' : 'destructive'} className="text-xs">{s.status}</Badge>}
                    </div>
                    {editingRule === s.id ? (
                      <div className="flex gap-2 mt-2">
                        <Input ref={editRef} defaultValue={s.rule} className="flex-1 text-sm" />
                        <Button size="sm" onClick={() => { approveSuggestion(s.id, editRef.current?.value); setEditingRule(null); }}>
                          <CheckCircle className="h-3 w-3 mr-1" /> Approve Edited
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingRule(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <p className="text-sm font-medium text-gray-900 mt-1">{s.rule}</p>
                    )}
                    {s.source_feedback && <p className="text-xs text-gray-500 mt-1 italic">From: "{s.source_feedback}"</p>}
                    {s.practitioner_code && <p className="text-xs text-gray-400">Practitioner: {s.practitioner_code}</p>}
                  </div>
                </div>
                {s.status === 'pending_review' && editingRule !== s.id && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" onClick={() => approveSuggestion(s.id)} className="bg-green-600 hover:bg-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingRule(s.id)}>
                      Edit & Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => rejectSuggestion(s.id)} className="text-red-600 hover:bg-red-50">
                      <XCircle className="h-3 w-3 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── ACTIVE RULES TAB ── */}
      {!loading && tab === 'knowledge' && (
        <div className="space-y-3">
          <div className="text-sm text-gray-600 bg-blue-50 rounded-lg p-3 flex items-center gap-2">
            <Shield className="h-4 w-4 text-teal-600" />
            These rules are actively injected into Dr. Peptide's system prompt. Every chat and protocol generation follows them.
          </div>
          {knowledge.length === 0 && <p className="text-center py-8 text-gray-500">No active rules yet. Approve suggestions from the Review Queue.</p>}
          {knowledge.map(k => (
            <Card key={k.id} className={!k.enabled ? 'opacity-50' : ''}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={k.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>{k.enabled ? 'Active' : 'Disabled'}</Badge>
                      {k.category && <Badge variant="outline" className="text-xs">{k.category}</Badge>}
                      {k.source_type && <Badge variant="outline" className="text-xs">{k.source_type}</Badge>}
                    </div>
                    <p className="text-sm font-medium">{k.rule}</p>
                    <p className="text-xs text-gray-400 mt-1">Added: {new Date(k.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => toggleKnowledge(k.id)}>
                      {k.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteKnowledge(k.id)} className="text-red-500 hover:bg-red-50">
                      Remove
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── RAW FEEDBACK TAB ── */}
      {!loading && tab === 'feedback' && (
        <div className="space-y-2">
          {feedback.length === 0 && <p className="text-center py-8 text-gray-500">No feedback yet.</p>}
          {feedback.map(f => (
            <Card key={f.id}>
              <CardContent className="p-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="text-sm">{f.feedback}</p>
                    {f.message && <p className="text-xs text-gray-400 mt-1 truncate">Context: {f.message.substring(0, 100)}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="text-xs">{f.type || 'chat'}</Badge>
                    {f.has_suggestion && <Badge className="bg-teal-100 text-teal-700 text-xs">Has suggestion</Badge>}
                    <span className="text-xs text-gray-400">{new Date(f.timestamp).toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── INTELLIGENCE STATS TAB ── */}
      {!loading && tab === 'stats' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Active Rules', value: stats.active_knowledge_rules || 0, icon: Brain, color: 'text-teal-600', desc: 'In Dr. Peptide\'s prompt' },
              { label: 'Pending Review', value: stats.pending_suggestions || 0, icon: Clock, color: 'text-yellow-600', desc: 'Awaiting approval' },
              { label: 'Contributing Practitioners', value: stats.contributing_practitioners || 0, icon: Users, color: 'text-green-600', desc: 'Signed up as practitioners' },
              { label: 'Total Feedback', value: stats.total_feedback || 0, icon: FileText, color: 'text-purple-600', desc: 'Submissions received' },
              { label: 'Tracked Patients', value: stats.tracked_patients || 0, icon: Users, color: 'text-indigo-600', desc: 'In patient files' },
              { label: 'Total Visits', value: stats.total_visits || 0, icon: Zap, color: 'text-teal-600', desc: 'Across all patients' },
              { label: 'Visits w/ Outcomes', value: stats.visits_with_outcome_data || 0, icon: TrendingUp, color: 'text-green-600', desc: 'With progress scores' },
              { label: 'Total Suggestions', value: stats.total_suggestions || 0, icon: Brain, color: 'text-orange-600', desc: 'AI-processed' },
            ].map((s, i) => (
              <Card key={i}>
                <CardContent className="p-4 text-center">
                  <s.icon className={`h-5 w-5 mx-auto mb-1 ${s.color}`} />
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs font-medium text-gray-700">{s.label}</div>
                  <div className="text-[10px] text-gray-400">{s.desc}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">How the Intelligence Loop Works</CardTitle></CardHeader>
            <CardContent className="text-sm text-gray-600 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-teal-700">1</div>
                <div><strong>Practitioner submits feedback</strong> — corrections, preferences, or outcome observations from clinical experience</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-teal-700">2</div>
                <div><strong>AI processes into structured rule</strong> — GPT-4.1 converts raw feedback into a precise clinical rule with category and confidence</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-teal-700">3</div>
                <div><strong>You review & approve</strong> — only rules you approve enter Dr. Peptide's intelligence. You can edit before approving.</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-teal-700">4</div>
                <div><strong>Patient outcomes feed back</strong> — progress scores from tracked patients aggregate into outcome insights that inform future protocols</div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-green-700">+</div>
                <div><strong>Dr. Peptide gets smarter</strong> — every approved rule and outcome data point makes the next protocol more accurate and personalized</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default KnowledgeAdmin;
